/**
 * Team Placement suggestion rules (admin UI).
 * Wording/rules align with Guide to HPS — suggestions are editable defaults only.
 */

/**
 * @param {number|string} size
 * @returns {{ promote: number, relegate: number } | null}
 */
export function getPromoRelegateCounts(size) {
  const n = Number(size);
  if (n === 4) return { promote: 1, relegate: 1 };
  if ([6, 7, 8, 10].includes(n)) return { promote: 2, relegate: 2 };
  if ([12, 14].includes(n)) return { promote: 3, relegate: 3 };
  return null;
}

/**
 * @param {{ exit_status?: string|null, season_exit_status?: string|null, is_forfeit?: boolean }} team
 */
export function isForfeitTeam(team) {
  if (!team) return false;
  if (team.is_forfeit === true) return true;
  return (
    String(team.exit_status || team.season_exit_status || '').toLowerCase() === 'forfeit'
  );
}

/**
 * Build action map for every team in a division (keyed by team_id).
 * Forfeit teams always relegate and consume relegation slots first;
 * remaining slots go to lowest-ranked non-forfeit teams.
 *
 * @param {Array<{ team_id: string, position: number, exit_status?: string, is_forfeit?: boolean }>} teams
 * @param {number|string} size — usually division.team_count
 * @returns {Map<string, 'promote'|'relegate'|'stay'|'manual'>}
 */
export function buildDivisionActionMap(teams, size) {
  const counts = getPromoRelegateCounts(size);
  const map = new Map();
  const list = Array.isArray(teams) ? [...teams] : [];

  if (!counts || list.length === 0) {
    list.forEach((t) => map.set(String(t.team_id), 'manual'));
    return map;
  }

  list.sort((a, b) => {
    const pa = Number(a.position) || 0;
    const pb = Number(b.position) || 0;
    if (pa !== pb) return pa - pb;
    return String(a.team_name || '').localeCompare(String(b.team_name || ''));
  });

  const forfeits = list.filter(isForfeitTeam);
  const active = list.filter((t) => !isForfeitTeam(t));

  const relegateIds = new Set();
  forfeits.forEach((t) => relegateIds.add(String(t.team_id)));

  const remainingSlots = Math.max(0, counts.relegate - forfeits.length);
  // Lowest-ranked active = highest position numbers
  const activeByWorst = [...active].sort((a, b) => {
    const pa = Number(a.position) || 0;
    const pb = Number(b.position) || 0;
    if (pa !== pb) return pb - pa;
    return String(b.team_name || '').localeCompare(String(a.team_name || ''));
  });
  for (let i = 0; i < remainingSlots && i < activeByWorst.length; i += 1) {
    relegateIds.add(String(activeByWorst[i].team_id));
  }

  const promoteIds = new Set();
  // Top N among non-forfeit by best position (forfeits never promote)
  const activeByBest = [...active].sort((a, b) => {
    const pa = Number(a.position) || 0;
    const pb = Number(b.position) || 0;
    if (pa !== pb) return pa - pb;
    return String(a.team_name || '').localeCompare(String(b.team_name || ''));
  });
  for (let i = 0; i < counts.promote && i < activeByBest.length; i += 1) {
    const id = String(activeByBest[i].team_id);
    if (!relegateIds.has(id)) promoteIds.add(id);
  }

  list.forEach((t) => {
    const id = String(t.team_id);
    if (relegateIds.has(id)) map.set(id, 'relegate');
    else if (promoteIds.has(id)) map.set(id, 'promote');
    else map.set(id, 'stay');
  });

  return map;
}

/**
 * @param {{
 *   action: string,
 *   emojiStyle?: 'mid_to_final' | string,
 *   isForfeit?: boolean,
 * }} opts
 */
export function formatSuggestionLabel({ action, emojiStyle, isForfeit }) {
  const up = emojiStyle === 'mid_to_final' ? '⏫️' : '⬆️';
  const down = emojiStyle === 'mid_to_final' ? '⏬️' : '⬇️';
  if (action === 'promote') return { action: 'promote', label: `${up} Promote` };
  if (action === 'relegate') {
    return {
      action: 'relegate',
      label: isForfeit ? `${down} Relegate (Forfeit)` : `${down} Relegate`,
    };
  }
  if (action === 'stay') return { action: 'stay', label: '➡️ Stay' };
  return { action: 'manual', label: '🔍 Review' };
}

/**
 * Pick next-season division id for an action (same canton list, skill-sorted).
 * @returns {string} division id or ''
 */
export function suggestNextDivisionId({ action, currentSkillMin, nextDivisions }) {
  const list = Array.isArray(nextDivisions) ? [...nextDivisions] : [];
  if (!list.length || !action || action === 'manual') return '';

  const cur = Number(currentSkillMin);
  if (!Number.isFinite(cur)) return '';

  list.sort((a, b) => Number(b.skill_level_min) - Number(a.skill_level_min));

  if (action === 'promote') {
    // Nearest higher skill (smallest skill_level_min still greater than current)
    const higher = list
      .filter((d) => Number(d.skill_level_min) > cur)
      .sort((a, b) => Number(a.skill_level_min) - Number(b.skill_level_min));
    return higher[0]?.id ? String(higher[0].id) : '';
  }

  if (action === 'relegate') {
    // Nearest lower skill (largest skill_level_min still less than current)
    const lower = list
      .filter((d) => Number(d.skill_level_min) < cur)
      .sort((a, b) => Number(b.skill_level_min) - Number(a.skill_level_min));
    return lower[0]?.id ? String(lower[0].id) : '';
  }

  if (action === 'stay') {
    const exact = list.find((d) => Number(d.skill_level_min) === cur);
    return exact?.id ? String(exact.id) : '';
  }

  return '';
}
